import { nonCityRoutes, routes } from '@/app/routes';
import * as userSelectors from '@/user/user-selectors';
import { useSelector } from 'react-redux';
import { generatePath, matchPath } from 'react-router-dom';
import { select } from 'redux-saga/effects';
import { NAV_ITEMS } from '../constants';
import { combinePaths } from './paths';

export const PLACE_PARAM = 'region';
export const PLACE_ROUTE_PREFIX = `/:${PLACE_PARAM}`;

/**
 * Returns given path prefixed with the given city/place
 * @param {string} place
 * @param {string} partialUrl
 * @returns {string}
 */
export function getPlaceSpecificUrl(place, partialUrl = '') {
  const placePrefix = generatePath(PLACE_ROUTE_PREFIX, {
    [PLACE_PARAM]: place,
  });
  return combinePaths(placePrefix, partialUrl);
}

/**
 * Creates a function that prefixes URL paths with the current place
 * @returns {function(string): string}
 */
export function usePlaceSpecificUrlFactory() {
  const currentPlace = useSelector(userSelectors.currentCity);

  return (...args) => getPlaceSpecificUrl(currentPlace, ...args);
}

/**
 * Returns the "root" URL of a given city (e.g. '/ca-placer')
 * @param {string} place
 * @returns {string}
 */
export function getPlaceSpecificRoot(place) {
  return getPlaceSpecificUrl(place, '');
}

/**
 * Prefixes a given react-router route expression with the city
 * to allow building routes for city-specific URLs
 * @param {string} partialRoute Route relative to a place's "root" URL,
 * e.g. "/:tab/:deckardId?"
 * @returns {string} Full route expression with the city/place component
 */
export function getPlaceSpecificRoute(partialRoute) {
  return combinePaths(PLACE_ROUTE_PREFIX, partialRoute);
}

/**
 * Helper saga to prefix a given URL with the current place.
 * See also `usePlaceSpecificUrlFactory` for a React hook equivalent
 * @param {string} partialUrl
 */
export function* getCurrentPlaceUrl(partialUrl) {
  const currentPlace = yield select(userSelectors.currentCity);

  return getPlaceSpecificUrl(currentPlace, partialUrl);
}

/**
 * Extracts a place ID from a place-prefixed URL path
 * @param {string} path e.g. "/ca-placer/home"
 * @returns {string|null} Place ID (e.g. "ca-placer") or null if place could not be parsed out
 */
export function getPlaceFromURLPath(path) {
  const matchedNonCityRoute = nonCityRoutes.find((route) =>
    matchPath(path, { path: route })
  );
  if (matchedNonCityRoute) {
    // "/login", "/logout", etc do not have a place
    return null;
  }

  const match = matchPath(path, {
    path: PLACE_ROUTE_PREFIX,
  });

  const parsedPlace = match?.params?.[PLACE_PARAM];

  if (Object.keys(NAV_ITEMS).includes(parsedPlace)) {
    return null;
  }

  return parsedPlace;
}

/**
 * Get the "tab name" (e.g. "home", "complaints", "list") from a
 * given place-specific URL
 * @param placeSpecificURL URL starting with a place name
 * @returns {string}
 */
export function getParentModuleFromPlaceSpecificURL(placeSpecificURL) {
  const match = matchPath(placeSpecificURL, {
    path: routes.DASHBOARD_ROUTE,
  });

  return match?.params.tab;
}
